import React, { useState, useEffect, useCallback } from "react";
import { useAppDispatch } from "@/app/hooks";
import { useSelector } from "react-redux";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { motion, useReducedMotion } from "framer-motion";
import { RootState } from "@/app/rootReducer";
import { selectActiveApp } from "@/app/globalSlices/appStripSlice";
import {
  selectActiveSessionCompanyName,
  selectIntacctId,
  selectUserAppSettings,
} from "@/modules/hub/features/UserAppSettings/selectors/userAppSettingsSelectors";
import { getUnifiedNavigation } from "@/navigation/unifiedNavigation";
import { NavigationItem } from "@/navigation/types";
import {
  ChevronRightIcon,
  ChevronUpDownIcon,
} from "@heroicons/react/24/outline";
import { StarIcon as StarIconOutline } from "@heroicons/react/24/outline";
import { StarIcon as StarIconSolid } from "@heroicons/react/24/solid";
import {
  pinItem,
  unpinItem,
  selectIsPinned,
  selectCanPinMore,
  getIconName,
} from "@/modules/hub/features/QuickLinks";
import { setActiveApp, AppId } from "@/app/globalSlices/appStripSlice";
import {
  Sidebar,
  SidebarHeader,
  SidebarContent,
  SidebarGroup,
  SidebarGroupContent,
  SidebarMenu,
  SidebarMenuItem,
  SidebarMenuButton,
  SidebarMenuSub,
  SidebarSeparator,
  SidebarFooter,
  useSidebar,
  APP_STRIP_WIDTH,
} from "@/components/ui/sidebar";
import permissionsService from "@/services/permissionsService";
import { filterNavigationByAccess } from "@/utils/navigationHelpers";
import CompanySwitcherModal from "@/components/CompanySwitcherModal/CompanySwitcherModal";
import YourCoachCard from "./YourCoachCard";
import MobileAppGrid from "@/components/AppStrip/MobileAppGrid";
import {
  expandItems,
  toggleWideSidebarItem,
} from "@/app/globalSlices/wideSidebarSlice";
import { openFileManagerDialog } from "@/app/globalSlices/fileManagerDialogSlice";
import { cn } from "@/utils/utils";

// Animated Submenu Component
function AnimatedSubmenu({
  isOpen,
  children,
}: {
  isOpen: boolean;
  children: React.ReactNode;
}) {
  return (
    <div
      className={cn(
        "grid transition-all duration-300 ease-in-out",
        isOpen ? "grid-rows-[1fr]" : "grid-rows-[0fr]",
      )}
    >
      <div className="overflow-hidden">
        <SidebarMenuSub className="ml-3 border-l border-sidebar-border/40 pl-3 py-1">
          {children}
        </SidebarMenuSub>
      </div>
    </div>
  );
}

// Pin button component for quick links
function PinButton({
  item,
  dispatch,
}: {
  item: NavigationItem;
  dispatch: ReturnType<typeof useAppDispatch>;
}) {
  const isPinned = useSelector((state: RootState) =>
    selectIsPinned(item.internalName)(state),
  );
  const canPinMore = useSelector((state: RootState) => selectCanPinMore(state));

  // Don't show pin button for items without valid href
  if (!item.href || item.href === "#" || item.children?.length) {
    return null;
  }

  const handlePinClick = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();

    if (isPinned) {
      dispatch(unpinItem(item.internalName));
    } else if (canPinMore) {
      dispatch(
        pinItem({
          id: item.internalName,
          name: item.name,
          href: item.href,
          iconName: getIconName(item.icon),
        }),
      );
    }
  };

  // Don't show if can't pin more and not already pinned
  if (!canPinMore && !isPinned) {
    return null;
  }

  return (
    <button
      className={cn(
        "ml-auto flex h-5 w-5 items-center justify-center rounded transition-all duration-200",
        "opacity-0 group-hover:opacity-100 focus:opacity-100",
        isPinned
          ? "text-yellow-500 hover:text-yellow-600"
          : "text-muted-foreground hover:text-primary",
      )}
      onClick={handlePinClick}
      title={isPinned ? "Remove from quick links" : "Add to quick links"}
      type="button"
    >
      {isPinned ? (
        <StarIconSolid className="h-3.5 w-3.5" />
      ) : (
        <StarIconOutline className="h-3.5 w-3.5" />
      )}
    </button>
  );
}

/** Keys of every nav item (at any depth) that has children. */
function collectGroupKeys(items: NavigationItem[]): string[] {
  const keys: string[] = [];
  for (const item of items) {
    if (item.children && item.children.length > 0) {
      keys.push(item.internalName || item.name);
      keys.push(...collectGroupKeys(item.children));
    }
  }
  return keys;
}

export default function WideSidebar() {
  const dispatch = useAppDispatch();
  const location = useLocation();
  const { isMobile, setOpenMobile } = useSidebar();
  const openItems = useSelector(
    (state: RootState) => state.wideSidebar?.openItems || {},
  );
  const activeApp = useSelector(selectActiveApp);

  const closeMobileNav = () => {
    if (isMobile) {
      setOpenMobile(false);
    }
  };

  // Determine active app from URL path (used on mount for page refreshes)
  const getActiveAppFromPath = useCallback((): string => {
    const path = location.pathname;
    if (path.startsWith("/stochastic")) return "stochastic";
    if (path.startsWith("/calls")) return "call-intelligence";
    // Coaching pages live under the Learning Center app (Coaches Progress Tracking).
    if (path.startsWith("/coaching")) return "learning-center";
    if (path.startsWith("/events")) return "events";
    if (path.startsWith("/product-storefront")) return "product-storefront";
    if (path.startsWith("/partner-network")) return "partner-network-new";
    if (path.startsWith("/sales")) return "sales";
    if (
      path.startsWith("/admin") ||
      path.startsWith("/email-management") ||
      path.startsWith("/capex")
    )
      return "admin";
    // Data Connector is under dashboards
    if (path.startsWith("/hub/data-connector")) return "dashboards";
    // Learning Center and Role Playing
    if (
      path.startsWith("/hub/learning-center") ||
      path.startsWith("/games/door-to-done")
    )
      return "learning-center";
    // Events
    if (path.startsWith("/event-registration")) return "events";
    // Community groups
    if (path.startsWith("/hub/community")) return "community";
    // Default to resources for /hub and other paths
    return "resources";
  }, [location.pathname]);

  const activeCompanyName = useSelector(selectActiveSessionCompanyName);
  const activeCompanyIntacctId = useSelector(selectIntacctId);
  const userAppSettings = useSelector(selectUserAppSettings);

  const rawNav = getUnifiedNavigation();
  const { hasPermission, hasRole, hasCertainPathCompany } =
    permissionsService();

  const filteredNav = filterNavigationByAccess(
    rawNav,
    hasPermission,
    hasRole,
    hasCertainPathCompany(),
  );

  const [companySwitcherOpen, setCompanySwitcherOpen] = useState(false);
  const [isImpersonating, setIsImpersonating] = useState(false);

  useEffect(() => {
    const impersonateEmployeeUuid = localStorage.getItem(
      "impersonateEmployeeUuid",
    );
    setIsImpersonating(!!impersonateEmployeeUuid);
  }, []);

  // Sync active app from URL to Redux on initial mount only (for page refreshes)
  const [hasInitialized, setHasInitialized] = useState(false);

  useEffect(() => {
    if (!hasInitialized) {
      const urlApp = getActiveAppFromPath();
      if (urlApp !== "resources" && urlApp !== activeApp) {
        dispatch(setActiveApp(urlApp as AppId));
      }
      setHasInitialized(true);
    }
  }, [hasInitialized, getActiveAppFromPath, activeApp, dispatch]);

  const userName =
    userAppSettings?.firstName && userAppSettings?.lastName
      ? `${userAppSettings.firstName} ${userAppSettings.lastName}`
      : userAppSettings?.email || "User";

  const canOpenCompanySwitcher = hasPermission("company-switcher");

  function handleToggleItem(itemKey: string) {
    dispatch(toggleWideSidebarItem(itemKey));
  }

  // Helper function to check if a navigation item has children
  function itemHasChildren(href: string, items: NavigationItem[]): boolean {
    for (const item of items) {
      if (item.href === href) {
        return !!(item.children && item.children.length > 0);
      }
      if (item.children) {
        if (itemHasChildren(href, item.children)) {
          return true;
        }
      }
    }
    return false;
  }

  const reducedMotion = useReducedMotion();

  function isActiveRoute(href: string): boolean {
    if (href === "#") return false;

    // Parse the href to separate pathname and query params
    const hrefUrl = new URL(href, window.location.origin);
    const hrefPathname = hrefUrl.pathname;
    const hrefSearch = hrefUrl.search;

    // If href has query params, require exact match of both pathname and search
    if (hrefSearch) {
      return (
        location.pathname === hrefPathname && location.search === hrefSearch
      );
    }

    // For hrefs without query params, check if it matches the pathname
    if (location.search) {
      const currentParams = new URLSearchParams(location.search);
      if (currentParams.has("filter")) {
        return false;
      }
    }

    // Exact match for root section routes
    if (hrefPathname === "/hub" || hrefPathname === "/calls") {
      return (
        location.pathname === hrefPathname ||
        location.pathname === `${hrefPathname}/dashboard`
      );
    }

    // Exact match always works
    if (location.pathname === hrefPathname) {
      return true;
    }

    // For prefix matching, only allow it if the item has children
    if (location.pathname.startsWith(hrefPathname + "/")) {
      const hasChildren = itemHasChildren(href, activeNavItems);
      return hasChildren;
    }

    return false;
  }

  function renderNavigation(items: NavigationItem[], depth = 0) {
    return items.map((item, index) => {
      const itemKey = item.internalName || item.name;
      const hasChildren = item.children && item.children.length > 0;
      const isOpen = !!openItems[itemKey];
      const isActive = item.href ? isActiveRoute(item.href) : false;

      if (item.isDivider) {
        // A divider that opens the menu is a heading only: no rule above it.
        const leadsMenu = depth === 0 && index === 0;
        return (
          <li
            className={cn("px-2", leadsMenu ? "mb-1 mt-0.5" : "my-1.5")}
            data-testid={`nav-divider-${itemKey}`}
            key={itemKey}
          >
            {!leadsMenu && <SidebarSeparator className="mx-0" />}
            {item.name && (
              <div className="mt-1.5 text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">
                {item.name}
              </div>
            )}
          </li>
        );
      }

      if (hasChildren) {
        return (
          <SidebarMenuItem key={itemKey}>
            <SidebarMenuButton
              className={cn(
                "text-[13px] py-1.5 px-2 rounded-md",
                depth > 0 && "text-[12px]",
                isActive && "bg-sidebar-accent text-sidebar-accent-foreground",
              )}
              onClick={() => handleToggleItem(itemKey)}
            >
              <span className="flex-1">{item.name}</span>
              {item.badge && (
                <span className="px-1.5 py-0.5 text-[10px] font-bold rounded-full bg-primary text-white mr-1">
                  {item.badge}
                </span>
              )}
              <ChevronRightIcon
                className={cn(
                  "h-3 w-3 text-muted-foreground transition-transform duration-200",
                  isOpen && "rotate-90",
                )}
              />
            </SidebarMenuButton>
            <AnimatedSubmenu isOpen={isOpen}>
              {renderNavigation(item.children!, depth + 1)}
            </AnimatedSubmenu>
          </SidebarMenuItem>
        );
      }

      // External URL
      const isExternalUrl =
        item.href?.startsWith("http://") || item.href?.startsWith("https://");

      if (isExternalUrl) {
        return (
          <SidebarMenuItem className="group" key={itemKey}>
            <div className="flex items-center w-full">
              <SidebarMenuButton
                asChild
                className={cn(
                  "text-[13px] py-1.5 px-2 rounded-md",
                  depth > 0 && "text-[12px]",
                  isActive &&
                    "bg-sidebar-accent text-sidebar-accent-foreground",
                  "transition-all duration-200 flex-1",
                )}
              >
                <a
                  href={item.href}
                  onClick={closeMobileNav}
                  rel="noopener noreferrer"
                  target="_blank"
                >
                  <span className="flex-1">{item.name}</span>
                </a>
              </SidebarMenuButton>
              <PinButton dispatch={dispatch} item={item} />
            </div>
          </SidebarMenuItem>
        );
      }

      // Dialog item
      if (item.isDialog) {
        return (
          <SidebarMenuItem key={itemKey}>
            <SidebarMenuButton
              className={cn(
                "text-[13px] py-1.5 px-2 rounded-md",
                depth > 0 && "text-[12px]",
                isActive && "bg-sidebar-accent text-sidebar-accent-foreground",
              )}
              onClick={() => {
                closeMobileNav();
                if (item.dialogAction === "fileManager") {
                  dispatch(openFileManagerDialog());
                }
              }}
            >
              <span className="flex-1">{item.name}</span>
            </SidebarMenuButton>
          </SidebarMenuItem>
        );
      }

      // Regular link. The active state is a navy bar that slides between
      // items (shared layoutId); hover nudges the label 2px right.
      return (
        <SidebarMenuItem className="group" key={itemKey}>
          <div className="flex items-center w-full">
            <SidebarMenuButton
              asChild
              className={cn(
                "relative text-[13px] py-1.5 px-2 rounded-md",
                depth > 0 && "text-[12px]",
                isActive && "bg-sidebar-accent text-sidebar-accent-foreground",
                "transition-colors duration-200 flex-1",
              )}
            >
              <Link onClick={closeMobileNav} to={item.href ?? "#"}>
                {isActive && (
                  <motion.span
                    aria-hidden
                    className="absolute left-0 top-1/2 h-4 w-[3px] -translate-y-1/2 rounded-full bg-primary"
                    layoutId="wide-sidebar-active"
                    transition={
                      reducedMotion
                        ? { duration: 0 }
                        : { type: "spring", stiffness: 500, damping: 38 }
                    }
                  />
                )}
                <span
                  className={cn(
                    "flex-1 transition-transform duration-200 ease-out",
                    !reducedMotion && "group-hover:translate-x-0.5",
                  )}
                >
                  {item.name}
                </span>
                {item.badge && (
                  <span className="px-1.5 py-0.5 text-[10px] font-bold rounded-full bg-primary text-white">
                    {item.badge}
                  </span>
                )}
              </Link>
            </SidebarMenuButton>
            <PinButton dispatch={dispatch} item={item} />
          </div>
        </SidebarMenuItem>
      );
    });
  }

  // Extract navigation sections
  const hubSection = filteredNav.find((item) => item.internalName === "hub");
  const stochasticSection = filteredNav.find(
    (item) => item.internalName === "stochastic",
  );
  const eventRegistrationSection = filteredNav.find(
    (item) => item.internalName === "event-registration",
  );
  const callIntelligenceSection = filteredNav.find(
    (item) => item.internalName === "call-intelligence",
  );
  const eventsSection = filteredNav.find(
    (item) => item.internalName === "events",
  );
  const productStorefrontSection = filteredNav.find(
    (item) => item.internalName === "product-storefront",
  );
  const learningCenterSection = filteredNav.find(
    (item) => item.internalName === "learning-center",
  );
  const emailManagementSection = filteredNav.find(
    (item) => item.internalName === "email_management",
  );
  const partnerNetworkSection = filteredNav.find(
    (item) => item.internalName === "partner_network",
  );
  const communitySection = filteredNav.find(
    (item) => item.internalName === "community",
  );
  const salesSection = filteredNav.find(
    (item) => item.internalName === "sales",
  );
  const internalAdminSection = filteredNav.find(
    (item) => item.internalName === "admin",
  );
  const companyAdminSection = filteredNav.find(
    (item) => item.internalName === "company-admin",
  );
  const adminSection = internalAdminSection || companyAdminSection;
  const labsSection = filteredNav.find((item) => item.internalName === "labs");

  // Get the direct children items
  const hubItems = hubSection?.children || [];
  const eventItems = eventRegistrationSection?.children || [];
  const stochasticItems = stochasticSection?.children || [];
  const callIntelligenceItems = callIntelligenceSection?.children || [];
  const eventsItems = eventsSection?.children || [];
  const productStorefrontItems = productStorefrontSection?.children || [];
  const learningCenterItems = learningCenterSection?.children || [];
  const emailManagementItems = emailManagementSection?.children || [];
  const partnerNetworkItems = partnerNetworkSection?.children || [];
  const communityItems = communitySection?.children || [];
  const salesItems = salesSection?.children || [];
  const adminItems = adminSection?.children || [];
  const labsItems = labsSection?.children || [];

  // Determine which items to show based on active app
  const getActiveNavItems = () => {
    switch (activeApp) {
      case "resources":
        return hubItems.filter(
          (item) =>
            item.internalName !== "home" &&
            item.internalName !== "data_connector" &&
            item.internalName !== "event-directory" &&
            item.internalName !== "product-directory" &&
            item.internalName !== "community",
        );
      case "stochastic":
        return stochasticItems;
      case "dashboards":
        return hubItems.filter(
          (item) => item.internalName === "data_connector",
        );
      case "call-intelligence":
        return callIntelligenceItems;
      case "events":
        return eventsItems;
      case "product-storefront":
        return productStorefrontItems;
      case "learning-center":
        return learningCenterItems;
      case "labs":
        return labsItems;
      case "partner-network-new":
        return partnerNetworkItems;
      case "community":
        return communityItems;
      case "sales":
        return salesItems;
      case "admin":
        return [...adminItems, ...emailManagementItems];
      default:
        return hubItems;
    }
  };

  const activeNavItems = getActiveNavItems();

  // Whenever the sub-menu changes app, open every collapsible group in it so
  // nothing the member needs is hidden behind a chevron.
  const groupKeys = collectGroupKeys(activeNavItems).join("|");
  useEffect(() => {
    if (groupKeys) {
      dispatch(expandItems(groupKeys.split("|")));
    }
  }, [activeApp, groupKeys, dispatch]);

  return (
    <div className="flex-shrink-0 h-full overflow-hidden">
      <Sidebar
        className="h-full border-r border-sidebar-border/60"
        collapsible="offcanvas"
        leftOffset={APP_STRIP_WIDTH}
        side="left"
        variant="sidebar"
      >
        <SidebarHeader className="h-14 border-b border-sidebar-border/60 p-3 flex justify-center">
          {canOpenCompanySwitcher ? (
            <button
              className="flex w-full items-center justify-between text-left hover:bg-sidebar-accent/50 rounded-md p-2 -m-2 transition-colors"
              onClick={() => setCompanySwitcherOpen(true)}
              type="button"
            >
              <div className="min-w-0 flex-1">
                <div className="text-sm font-medium truncate">
                  {activeCompanyName || "No Company"}
                </div>
                {activeCompanyIntacctId && (
                  <div className="text-xs text-muted-foreground">
                    ID: {activeCompanyIntacctId}
                  </div>
                )}
              </div>
              <ChevronUpDownIcon className="h-4 w-4 text-muted-foreground flex-shrink-0 ml-2" />
            </button>
          ) : (
            <div className="min-w-0">
              <div className="text-sm font-medium truncate">
                {activeCompanyName || "No Company"}
              </div>
              {activeCompanyIntacctId && (
                <div className="text-xs text-muted-foreground">
                  ID: {activeCompanyIntacctId}
                </div>
              )}
            </div>
          )}
        </SidebarHeader>

        <SidebarContent className="px-1 overflow-y-auto">
          {isMobile && (
            <div className="border-b border-sidebar-border/60">
              <MobileAppGrid />
            </div>
          )}
          {activeNavItems.length > 0 && (
            <SidebarGroup>
              <SidebarGroupContent>
                <SidebarMenu className="space-y-0">
                  {renderNavigation(activeNavItems)}
                </SidebarMenu>
              </SidebarGroupContent>
            </SidebarGroup>
          )}
        </SidebarContent>
        <SidebarFooter className="border-t border-sidebar-border/60 p-2">
          <YourCoachCard companyKey={activeCompanyName} />
        </SidebarFooter>
      </Sidebar>

      <CompanySwitcherModal
        onClose={() => setCompanySwitcherOpen(false)}
        open={companySwitcherOpen}
      />
    </div>
  );
}
